/**
 * List-view config for the Dashboards page: search/sort/filter wiring for the shared list-page pattern
 * (`useListControls` + `filterSortItems` + `ListToolbar`).
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';

import type { FilterGroup, SortOption } from '@/_shared/components/list/ListToolbar';
import type { ListDeriveConfig, SortState } from '@/_shared/lib/list-controls';

/** The backend row shape returned by `dashboard.list`. */
export type DashboardListRow = Awaited<ReturnType<RouterClient<AppRouter>['dashboard']['list']>>['dashboards'][number];

/** Dashboard-list view model: a backend row enriched with derived per-user star state. */
export type DashboardListViewItem = DashboardListRow & { isStarred: boolean };

export type DashboardSortKey = 'updated' | 'title' | 'panels';
export type DashboardFilterKey = 'visibility' | 'starred';

export const DASHBOARD_SORT_KEYS: readonly DashboardSortKey[] = ['updated', 'title', 'panels'];

export const DASHBOARD_DEFAULT_SORT: SortState<DashboardSortKey> = { key: 'updated', dir: 'desc' };

export const DASHBOARD_SORT_OPTIONS: readonly SortOption[] = [
  { value: 'updated', label: 'Recently updated' },
  { value: 'title', label: 'Name' },
  { value: 'panels', label: 'Panel count' },
];

const VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Public' },
  { value: 'private', label: 'Private' },
] as const;

const STARRED_OPTIONS = [{ value: 'starred', label: 'Starred only' }] as const;

export const DASHBOARD_FILTER_GROUPS: readonly FilterGroup[] = [
  { key: 'visibility', label: 'Visibility', options: VISIBILITY_OPTIONS },
  { key: 'starred', label: 'Starred', options: STARRED_OPTIONS },
];

export const DASHBOARD_FILTER_KEYS: readonly DashboardFilterKey[] = ['visibility', 'starred'];

/** Allowed values per filter, so `useListControls` can reject bogus URL params (e.g. `?f_visibility=nope`). */
export const DASHBOARD_FILTER_VALUES: Partial<Record<DashboardFilterKey, readonly string[]>> = {
  visibility: VISIBILITY_OPTIONS.map((o) => o.value),
  starred: STARRED_OPTIONS.map((o) => o.value),
};

/** "3 panels" / "1 panel" — the card + row meta label. */
export function panelCountLabel(panelCount: number): string {
  return `${panelCount} ${panelCount === 1 ? 'panel' : 'panels'}`;
}

const byString = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

export const DASHBOARD_DERIVE_CONFIG: ListDeriveConfig<DashboardListViewItem, DashboardSortKey, DashboardFilterKey> = {
  searchText: (d) => [d.title, d.description ?? ''],
  comparators: {
    title: (a, b) => a.title.localeCompare(b.title),
    updated: (a, b) => byString(a.updatedAt, b.updatedAt),
    panels: (a, b) => a.spec.panels.length - b.spec.panels.length,
  },
  filterPredicates: {
    visibility: (d, value) => (value === 'public' ? d.isPublic : !d.isPublic),
    starred: (d) => d.isStarred,
  },
};
